import { PublicDeck } from '@mtg/shared';
import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { api } from '../api.ts';
import { DeckView } from '../components/DeckView.tsx';
import { ThemeToggle } from '../components/ThemeToggle.tsx';
import { plural } from '../format.ts';
import { useApi } from '../useApi.ts';

/** A shared deck, read-only and without prices, for anyone with its own link. */
export function PublicDeckPage({ slug }: { slug: string }) {
  return <PublicDeckView source={`/api/public/decks/${encodeURIComponent(slug)}`} />;
}

/** A deck opened from the shared gallery of all decks (/decks). */
export function GalleryDeckPage({ id }: { id: string }) {
  return <PublicDeckView source={`/api/public/gallery/decks/${encodeURIComponent(id)}`} back={<Link href="/decks">← All decks</Link>} />;
}

function PublicDeckView({ source, back }: { source: string; back?: ReactNode }) {
  const { data, error } = useApi(() => api(source, PublicDeck), [source]);
  if (error) {
    return (
      <main className="main stack">
        {back && <p className="small">{back}</p>}
        <p className="error">{error}</p>
      </main>
    );
  }
  if (!data) return <main className="main muted">Loading…</main>;
  const count = data.cards.reduce((n, c) => n + c.quantity, 0);
  return (
    <>
      <main className="main stack">
        {back && <p className="small">{back}</p>}
        <h1>{data.name}</h1>
        <p className="lead">
          {data.format} · {plural(count, 'card')}
        </p>
        {data.notes && <p>{data.notes}</p>}
        <DeckView items={data.cards.map((c, i) => ({ key: String(i), ...c }))} commanders={data.commanders} linkCards={false} showPrices={false} />
      </main>
      <PublicFooter />
    </>
  );
}

export function PublicFooter() {
  return (
    <footer className="footer muted small">
      <p>
        Card images and data from <a href="https://scryfall.com">Scryfall</a>.
      </p>
      <ThemeToggle />
    </footer>
  );
}
